import { memo, useState, useCallback } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Spinner,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
} from '@coreai/fluentui-react';
import {
  CheckmarkCircleFilled,
  DismissCircleFilled,
  SubtractCircleFilled,
  ClockFilled,
  CopyRegular,
  OpenRegular,
} from '@fluentui/react-icons';
import type { NodeExecutionStatus } from '@/types/monitoring';
import { formatDuration } from '@/utils/dateTime';

export type ExecutionOverlayProps = {
  status: NodeExecutionStatus;
  duration?: number | undefined;
  /** Iteration count for loop/agent nodes (displayed in tooltip) */
  iterationCount?: number | undefined;
  /** Position of the overlay - defaults to top-right corner of the node */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** Size of the overlay - small for tool nodes, medium for regular nodes */
  size?: 'small' | 'medium';
  /** Error message for failed executions */
  errorMessage?: string | undefined;
  /** Error code for failed executions */
  errorCode?: string | undefined;
  /** Callback when "View Details" is clicked */
  onViewDetails?: () => void;
};

const useStyles = makeStyles({
  overlay: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusCircular,
    boxShadow: tokens.shadow4,
    zIndex: 10,
  },
  // Positions
  topRight: {
    top: '-8px',
    right: '-8px',
  },
  topLeft: {
    top: '-8px',
    left: '-8px',
  },
  bottomRight: {
    bottom: '-8px',
    right: '-8px',
  },
  bottomLeft: {
    bottom: '-8px',
    left: '-8px',
  },
  // Sizes
  medium: {
    paddingBlock: tokens.spacingVerticalXXS,
    paddingInline: tokens.spacingHorizontalSNudge,
  },
  small: {
    padding: '1px 4px',
  },
  // Icons
  succeededIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  failedIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  runningIcon: {
    color: tokens.colorPaletteBlueForeground2,
  },
  skippedIcon: {
    color: tokens.colorNeutralForeground3,
  },
  pendingIcon: {
    color: tokens.colorNeutralForeground4,
  },
  iconMedium: {
    fontSize: tokens.fontSizeBase400,
  },
  iconSmall: {
    fontSize: tokens.fontSizeBase200,
  },
  duration: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'nowrap',
  },
  durationSmall: {
    fontSize: tokens.fontSizeBase100,
  },
  // Error popover styles
  errorPopover: {
    padding: tokens.spacingVerticalM,
    maxWidth: '320px',
  },
  errorHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  errorTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },
  errorCode: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
  },
  errorMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '120px',
    overflow: 'auto',
    marginBottom: tokens.spacingVerticalS,
  },
  errorActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
  // Clickable overlay for error states
  clickable: {
    cursor: 'pointer',
  },
});

const formatOverlayDuration = (ms: number): string => formatDuration(ms, { style: 'overlay' });

const getStatusLabel = (status: NodeExecutionStatus): string => {
  switch (status) {
    case 'succeeded': return 'Succeeded';
    case 'failed': return 'Failed';
    case 'running': return 'Running';
    case 'skipped': return 'Skipped';
    case 'pending': return 'Pending';
    default: return status;
  }
};

export const ExecutionOverlay = memo(({
  status,
  duration,
  iterationCount,
  position = 'top-right',
  size = 'medium',
  errorMessage,
  errorCode,
  onViewDetails,
}: ExecutionOverlayProps) => {
  const styles = useStyles();
  const [isErrorPopoverOpen, setIsErrorPopoverOpen] = useState(false);

  const positionClass = {
    'top-right': styles.topRight,
    'top-left': styles.topLeft,
    'bottom-right': styles.bottomRight,
    'bottom-left': styles.bottomLeft,
  }[position];

  const sizeClass = size === 'small' ? styles.small : styles.medium;
  const iconSizeClass = size === 'small' ? styles.iconSmall : styles.iconMedium;

  const hasErrorDetails = status === 'failed' && (errorMessage || errorCode);

  const handleCopyError = useCallback(() => {
    const text = [
      errorCode && `Error Code: ${errorCode}`,
      errorMessage && `Message: ${errorMessage}`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(text);
  }, [errorCode, errorMessage]);

  const handleViewDetails = useCallback(() => {
    setIsErrorPopoverOpen(false);
    onViewDetails?.();
  }, [onViewDetails]);

  const renderIcon = () => {
    switch (status) {
      case 'succeeded':
        return <CheckmarkCircleFilled className={mergeClasses(styles.succeededIcon, iconSizeClass)} />;
      case 'failed':
        return <DismissCircleFilled className={mergeClasses(styles.failedIcon, iconSizeClass)} />;
      case 'running':
        return <Spinner size="extra-tiny" className={styles.runningIcon} />;
      case 'skipped':
        return <SubtractCircleFilled className={mergeClasses(styles.skippedIcon, iconSizeClass)} />;
      case 'pending':
      default:
        return <ClockFilled className={mergeClasses(styles.pendingIcon, iconSizeClass)} />;
    }
  };

  const statusLabel = getStatusLabel(status);
  // Build tooltip content with status, duration, and iteration count
  let tooltipContent = statusLabel;
  if (duration !== undefined && status !== 'pending' && status !== 'skipped') {
    tooltipContent = `${statusLabel} in ${formatOverlayDuration(duration)}`;
  }
  if (iterationCount !== undefined) {
    tooltipContent += ` (${iterationCount} iteration${iterationCount !== 1 ? 's' : ''})`;
  }
  if (hasErrorDetails) {
    tooltipContent += ' - Click for details';
  }
  const ariaLabel = tooltipContent;

  const overlayContent = (
    <div
      className={mergeClasses(
        styles.overlay,
        positionClass,
        sizeClass,
        hasErrorDetails && styles.clickable
      )}
      data-status={status}
      role="status"
      aria-label={ariaLabel}
    >
      {renderIcon()}
      {duration !== undefined && status !== 'pending' && status !== 'skipped' && (
        <span className={mergeClasses(styles.duration, size === 'small' && styles.durationSmall)}>
          {formatOverlayDuration(duration)}
        </span>
      )}
    </div>
  );

  // For failed status with error details, show a popover on click
  if (hasErrorDetails) {
    return (
      <Popover
        open={isErrorPopoverOpen}
        onOpenChange={(_, data) => setIsErrorPopoverOpen(data.open)}
        positioning="below-start"
      >
        <PopoverTrigger disableButtonEnhancement>
          <Tooltip content={tooltipContent} relationship="label" positioning="below">
            {overlayContent}
          </Tooltip>
        </PopoverTrigger>
        <PopoverSurface className={styles.errorPopover}>
          <div className={styles.errorHeader}>
            <DismissCircleFilled className={styles.failedIcon} />
            <span className={styles.errorTitle}>Execution Failed</span>
            {errorCode && <span className={styles.errorCode}>{errorCode}</span>}
          </div>
          {errorMessage && (
            <div className={styles.errorMessage}>{errorMessage}</div>
          )}
          <div className={styles.errorActions}>
            <Button
              size="small"
              appearance="subtle"
              icon={<CopyRegular />}
              onClick={handleCopyError}
            >
              Copy
            </Button>
            {onViewDetails && (
              <Button
                size="small"
                appearance="primary"
                icon={<OpenRegular />}
                onClick={handleViewDetails}
              >
                View Details
              </Button>
            )}
          </div>
        </PopoverSurface>
      </Popover>
    );
  }

  // For other statuses, show simple tooltip
  return (
    <Tooltip content={tooltipContent} relationship="label" positioning="below">
      {overlayContent}
    </Tooltip>
  );
});

ExecutionOverlay.displayName = 'ExecutionOverlay';
